const EMPTY_LIST = [];

function reservationActionLooksDeleted(row) {
   if (!row || typeof row !== "object") return false;
   const actionRaw =
      row.action ??
      row.status ??
      row.type ??
      row.reservation?.action ??
      row.reservation?.status ??
      row.reservation?.type ??
      "";
   return /(delete|deleted|remove|removed)/i.test(String(actionRaw || ""));
}

export function isReservationSoftDeletedRow(row) {
   if (!row || typeof row !== "object") return false;
   if (reservationActionLooksDeleted(row)) return true;

   return !!(
      row.isDeleted ??
      row.is_deleted ??
      row.deletedAt ??
      row.deleted_at ??
      row.reservation?.isDeleted ??
      row.reservation?.is_deleted ??
      row.reservation?.deletedAt ??
      row.reservation?.deleted_at
   );
}

export function filterSoftDeletedReservationRows(items) {
   if (!Array.isArray(items) || !items.length) return EMPTY_LIST;
   const out = [];
   for (const row of items) {
      if (!isReservationSoftDeletedRow(row)) out.push(row);
   }
   return out;
}

export function extractMonthKeyFromQueryPayload(payload) {
   if (!payload || typeof payload !== "object") return "";
   const range = payload.range || null;
   const from = String(
      range?.startDateFrom ?? range?.from ?? range?.start ?? "",
   ).trim();
   const match = from.match(/^(\d{4}-\d{2})/);
   return match ? match[1] : "";
}

/**
 * La deschiderea calendarului luna poate veni din cache-ul RTK, încărcată înainte (calendarul era închis — de ex.
 * cât s-a lucrat în calendarul nou — și n-a primit evenimentele socketului). O astfel de lună se recitește o dată.
 * O lună încărcată după deschidere (sau fără timp) nu se recitește.
 */
export function shouldRefetchCachedMonthOnOpen(fulfilledTimeStamp, openedAt) {
   const loaded = Number(fulfilledTimeStamp);
   const opened = Number(openedAt);
   if (!Number.isFinite(loaded) || loaded <= 0) return false;
   if (!Number.isFinite(opened) || opened <= 0) return false;
   return loaded < opened;
}

export function shouldHydrateMonthQueryPayload(previousPayload, nextPayload) {
   if (!nextPayload) return false;
   if (previousPayload === nextPayload) return false;
   return true;
}

function monthRangeKey(range) {
   if (!range || typeof range !== "object") return "";
   return `${range.startDateFrom ?? range.from ?? ""}|${range.startDateTo ?? range.to ?? ""}`;
}

/** Ce s-a aplicat ultima dată în listă din luna RTK (pentru `isMonthPayloadUnchanged`). */
export function buildAppliedMonthRecord(payload, liveList, hasLocalProtection) {
   return {
      items: payload?.items ?? null,
      rangeKey: monthRangeKey(payload?.range),
      list: liveList ?? null,
      stable: !hasLocalProtection,
   };
}

/**
 * Luna recitită (întoarcere în aplicație, eveniment socket, reîncercare) vine des neschimbată: RTK păstrează aceleași
 * `items` (structural sharing), nou e doar `serverTime`. Atunci lista nu se mai aplică din nou — altfel calendarul se
 * reconstruiește integral (pe telefon secunde de fir principal) pentru aceleași date. Se aplică totuși dacă lista vie
 * s-a schimbat local de la ultima aplicare sau dacă atunci avea rânduri protejate (optimiste / șterse recent):
 * rezultatul aplicării depinde de timp și o modificare eșuată trebuie să revină la datele serverului.
 */
export function isMonthPayloadUnchanged(applied, nextPayload, liveList) {
   if (!applied || !nextPayload) return false;
   if (!applied.stable) return false;
   if (applied.list !== liveList) return false;
   if (!Array.isArray(nextPayload.items) || applied.items !== nextPayload.items) return false;
   return applied.rangeKey === monthRangeKey(nextPayload.range);
}

export function buildMonthSnapshotFromQueryPayload(payload) {
   const items = Array.isArray(payload?.items) ? payload.items : EMPTY_LIST;
   return {
      monthKey: extractMonthKeyFromQueryPayload(payload),
      items,
      range: payload?.range ?? null,
      appliedAt: new Date().toISOString(),
   };
}
